import {graphql} from 'gatsby'
import React from 'react'
import styled from 'styled-components'

import Button from '@/components/button'
import FeatureBlock from '@/components/feature-block'
import PrismicLink from '@/components/prismic-link'
import SmoothScrollLink from '@/components/smooth-scroll-link'
import {useGlobalStrings} from '@/providers/global-strings'
import {useSignupModal} from '@/providers/signup-modal'
import {BackgroundWrapper, Section} from '@/styles/grid'
import {isValidBackgroundColor} from '@/utils/is-valid-background-color'
import {isValidTagColor} from '@/utils/is-valid-tag-color'
import mq from '@/utils/media-query'

import type {LandingHeroSliceFragment} from '@/graphql-types'

export const LandingHeroSlice = ({slice}: {slice: LandingHeroSliceFragment}) => {
    const {
        background,
        cta_text: ctaText,
        cta_link: ctaLink,
        cta_destination: ctaDestination,
    } = slice.primary

    const {renderSignupModal} = useSignupModal()
    const globalStrings = useGlobalStrings()

    const color = isValidBackgroundColor(background) ? background : 'default'
    const showSignupCTA = ['accountingpartners', 'signupform'].includes(ctaDestination)
    const scrollToHubspotForm = ['hubspotform'].includes(ctaDestination)

    const openSignupModal = () => {
        renderSignupModal(
            ctaDestination === 'accountingpartners'
                ? {
                      signupProps: {
                          form: 'Partner',
                          isPartner: true,
                      },
                  }
                : {},
        )
    }

    return (
        <BackgroundWrapper $color={color}>
            <Section padding="small">
                <FeatureBlock
                    image={slice.primary.image}
                    videoId={slice.primary.wistia_video_id}
                    preTitle={slice.primary.headline}
                    preTitleBackgroundColor={
                        isValidTagColor(slice.primary.headline_background_color)
                            ? slice.primary.headline_background_color
                            : undefined
                    }
                    title={slice.primary.title}
                    body={slice.primary.lead_text.richText}
                    mirrored={!!slice.primary.inverse}
                    titleVariant="heading-large"
                    bodyProps={{$variant: 'paragraph-large'}}
                    headingLevel="h1"
                >
                    <ButtonsWrapper>
                        {showSignupCTA && (
                            <Button onClick={openSignupModal}>{globalStrings.get_started}</Button>
                        )}
                        {scrollToHubspotForm && (
                            <SmoothScrollLink selector="#hubspot-form">
                                <Button>{ctaText}</Button>
                            </SmoothScrollLink>
                        )}
                        {ctaText && ctaLink && (
                            <Button
                                as={PrismicLink}
                                $variant={showSignupCTA ? 'outline' : 'default'}
                                {...ctaLink}
                            >
                                {ctaText}
                            </Button>
                        )}
                    </ButtonsWrapper>
                </FeatureBlock>
            </Section>
        </BackgroundWrapper>
    )
}

const ButtonsWrapper = styled.div`
    display: flex;
    flex-direction: column;
    gap: ${({theme}) => theme.spacings.s12};

    ${mq.min('sm')} {
        flex-direction: row;
        gap: ${({theme}) => theme.spacings.s20};
    }
`

export const landingHeroSliceFragment = graphql`
    fragment LandingHeroSlice on PrismicSlicePageDataBodyLandingHero {
        __typename
        primary {
            headline
            headline_background_color
            title
            lead_text {
                richText
            }
            cta_text
            cta_link {
                ...PrismicLink
            }
            cta_destination
            inverse
            background
            image {
                alt
                copyright
                url
                dimensions {
                    height
                    width
                }
                gatsbyImageData(breakpoints: $imageBreakpoints, sizes: $twoColumnsAsymmetric)
            }
            wistia_video_id
        }
    }
`
